import classNames from 'classnames';
import { PageContext } from 'context/PageContext';
import { useFormSubmit, useNavigation } from 'hooks';
import React, { memo, useContext, useState } from 'react';
import { FieldValues, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { useDeviceQuery } from '@almbrand/usedevicequery';
import { clearPersistedState } from 'store/reducers';
import { removeRadioState } from 'store/slices/radioCardSlice';
import { completeBasketItem, getActiveBasketItem } from '../../../services/BasketJourneyService';
import { setSubmitOpportunityError, validateForm } from '../../../services/FormValidationService';
import { submitOpportunityLead } from '../../../services/OpportunityQuoteService';
import { isReceiptStepByPageId, isSummaryStepByPageId } from '../../../services/PackageService';
import { pushToDataLayer, trackBeginCheckoutViewItem, trackPurchase } from '../../../services/TrackingService';
import { parseDanishAmount } from '../../../utilities/format';
import { ButtonComponent } from '../ButtonComponent';
import styles from './ButtonNavigationComponent.module.scss';

export interface ButtonNavigationComponent {
	label: string;
	isValid: boolean;
	isSubmit: boolean;
	pageId: number;
	submit?: () => Promise<void>;
	isPackage?: boolean;
	trigger?: UseFormTrigger<FieldValues>;
	setValue?: UseFormSetValue<FieldValues>;
	url?: string;
	beforeSubmit?: () => void;
}
export const ButtonNavigationComponent: React.FC<ButtonNavigationComponent> = memo(
	({ trigger = () => {}, setValue = () => {}, ...props }) => {
		const dispatch = useAppDispatch();
		const pageContext = useContext(PageContext);
		const { isMobile } = useDeviceQuery();
		const { theme } = pageContext ?? {};
		const { resetFormField } = useFormSubmit();

		const { navigate } = useNavigation();

		const [isSubmitting, setIsSubmitting] = useState(false);
		const [basketError, setBasketError] = useState<string>();

		const { orderedPageList } = useAppSelector((state) => state.pages);
		const calculatorForm = useAppSelector((state) => state.calculatorForm);
		const calculationState = useAppSelector((state) => state.calculationState);

		const currentInx = orderedPageList.indexOf(props.pageId);

		const submitBasketItem = () => {
			const selectedPackage = calculatorForm.SELECTED_PACKAGE?.data?.value;
			const monthlyPrice = parseDanishAmount(calculatorForm.SELECTED_PACKAGE_MONTHLY_PRICE?.data?.value);
			if (!selectedPackage || !Number.isFinite(monthlyPrice) || monthlyPrice < 0) {
				setBasketError('Vælg en pakke med en beregnet pris, før du fortsætter.');
				return;
			}
			setIsSubmitting(true);
			setBasketError(undefined);
			try {
				const nextUrl = completeBasketItem({ calculatorForm, calculationState, selectedPackage });
				if (nextUrl) window.location.assign(nextUrl);
			} catch {
				setBasketError('Vi kunne ikke gemme beregningen. Prøv igen.');
				setIsSubmitting(false);
			}
		};

		const submit = async () => {
			if (isSubmitting) {
				return;
			}

			props.beforeSubmit?.();

			const validSubmit = await submitInsurance();
			if (!validSubmit) {
				setIsSubmitting(false);
				return;
			}

			if (props.isPackage) {
				const metaArea = pageContext.metaArea;
				trackBeginCheckoutViewItem(metaArea, false);
				pushToDataLayer();
			}

			if (props.isValid) {
				if (props.isPackage && getActiveBasketItem()) {
					submitBasketItem();
					return;
				}
				await props.submit?.();
				navigate(props.pageId, true);
				// url set by CMS
				if (props.url) {
					location.href = props.url;
				}
			}
			// force validation of forms, så fields can display errors
			else {
				void trigger?.();
			}
		};

		const navigateBack = () => {
			if (isSubmitting) {
				return;
			}
			if (props.isPackage) {
				// remove customer selection, since i price change on reentry to calculation, doesn't trigger formchange
				dispatch(removeRadioState('SELECTED_PACKAGE'));
				resetFormField('SELECTED_PACKAGE');
				void trigger?.();
			}

			if (currentInx > 0) {
				navigate(props.pageId, false);
			}
		};

		const submitInsurance = async () => {
			if (isSummaryStepByPageId(orderedPageList[currentInx])) {
				setIsSubmitting(true);
				const res = validateForm(setValue, pageContext.metaArea) ? await doSendToSalesForce() : false;
				if (!res) {
					setIsSubmitting(false);
					return false;
				}
				const metaArea = pageContext.metaArea;
				await trackPurchase(metaArea);
				pushToDataLayer();
				return res;
			}

			if (isReceiptStepByPageId(orderedPageList[currentInx])) {
				dispatch(clearPersistedState());
			}

			return true;
		};

		const doSendToSalesForce = async () => {
			try {
				await submitOpportunityLead(pageContext);
				return true;
			} catch (e) {
				setSubmitOpportunityError(setValue);
				return false;
			} finally {
				setIsSubmitting(false);
			}
		};

		const getButtonType = () => {
			if (!props.isSubmit) {
				return isMobile ? 'circle' : 'tertiary';
			}
			return 'primary';
		};

		return (
			<div
				className={classNames(
					styles.ButtonNavigation,
					props.isPackage && styles.ButtonNavigation__package,
					props.isPackage && props.isValid && props.isSubmit && styles.ButtonNavigation__package__valid,
					props.isSubmit && styles.ButtonNavigation__submit,
					!props.isSubmit && styles.ButtonNavigation__back
				)}
			>
				{' '}
				{basketError && <p role='alert'>{basketError}</p>}
				<ButtonComponent
					type={'button'}
					isValid={props.isValid && !isSubmitting}
					label={props.label}
					buttonType={getButtonType()}
					onClick={props.isSubmit ? submit : navigateBack}
					iconProps={
						!props.isSubmit && {
							themeName: theme,
							icon: 'arrow_left_tail_long',
							width: '24',
						}
					}
					isLoading={props.isSubmit && isSubmitting}
				>
					{''}
				</ButtonComponent>
			</div>
		);
	}
);

ButtonNavigationComponent.displayName = 'ButtonNavigationComponent';
